import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@ais-ui/ui-core';
import { LongitudeSpans } from '../utils/antimeridian';

export interface AntimeridianDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Longitude span information for display */
  spans: LongitudeSpans;
  /** Called when user confirms the shape crosses the antimeridian */
  onConfirmCrossing: () => void;
  /** Called when user wants to redraw the shape */
  onRedraw: () => void;
  /** The type of geometry being drawn */
  geometryType?: 'Polygon' | 'LineString';
}

/**
 * Dialog that asks the user to confirm their intent when drawing a shape
 * that might cross the antimeridian (International Date Line).
 *
 * This is necessary because coordinates outside [-180, 180] can indicate
 * that the user drew across the antimeridian intentionally, or just panned
 * the map while drawing.
 */
export function AntimeridianDialog({
  open,
  spans,
  onConfirmCrossing,
  onRedraw,
  geometryType = 'Polygon',
}: AntimeridianDialogProps) {
  const shapeLabel = geometryType === 'LineString' ? 'line' : 'polygon';

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Did you intend to cross the antimeridian?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Your {shapeLabel} appears to cross the International Date Line
                (180° longitude).
              </p>
              <p className="text-sm">
                This creates a {shapeLabel} spanning approximately{' '}
                <strong>{spans.antimeridianSpan.toFixed(0)}°</strong> of
                longitude across the dateline.
              </p>
              <p className="text-sm text-muted-foreground">
                If you didn't intend this, you may have panned the map while
                drawing.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onRedraw}>
            No, let me redraw
          </AlertDialogCancel>
          <AlertDialogAction onClick={onConfirmCrossing}>
            Yes, this is correct
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
